import { useEffect, useRef, useState } from "react";

export type PremisesLocation = {
  name?: string;
  county?: string;
  eircode: string;
  site_address: string;
  latitude: string;
  longitude: string;
  location_source: string;
  pin_adjusted: boolean;
  location_confirmed: boolean;
};

type LatLngLiteral = { lat: number; lng: number };
type MapClickEvent = { latLng?: { lat: () => number; lng: () => number } };
type MapLike = {
  setCenter: (point: LatLngLiteral) => void;
  setZoom: (zoom: number) => void;
  addListener: (event: string, listener: (event: MapClickEvent) => void) => { remove: () => void };
};
type MarkerLike = {
  setMap: (map: MapLike | null) => void;
  setPosition: (point: LatLngLiteral) => void;
  getPosition: () => { lat: () => number; lng: () => number } | null;
  addListener: (event: string, listener: () => void) => { remove: () => void };
};
type AddressComponent = { long_name: string; short_name: string; types: string[] };
type GeocoderResult = {
  place_id: string;
  formatted_address: string;
  address_components: AddressComponent[];
  geometry: { location: { lat: () => number; lng: () => number } };
};
type GeocoderLike = {
  geocode: (
    request: Record<string, unknown>,
    callback: (results: GeocoderResult[] | null, status: string) => void,
  ) => void;
};
type GoogleMapsNamespace = {
  maps: {
    Map: new (element: HTMLElement, options: Record<string, unknown>) => MapLike;
    Marker: new (options: Record<string, unknown>) => MarkerLike;
    Geocoder: new () => GeocoderLike;
  };
};

declare global {
  interface Window {
    google?: GoogleMapsNamespace;
    __namoGoogleMapsReady?: () => void;
  }
}

type SearchResult = {
  place_id: string;
  display_name: string;
  lat: number;
  lng: number;
  postal_code: string;
};

const irelandCentre = { lat: 53.42, lng: -7.94 };
let mapsLoader: Promise<GoogleMapsNamespace> | null = null;

export function validEircode(value: string) {
  return /^(?:[AC-FHKNPRTV-Y]\d{2}|D6W)\s?[0-9AC-FHKNPRTV-Y]{4}$/i.test(value.trim());
}

function normalizeEircode(value: string) {
  return value.toUpperCase().replace(/\s+/g, "");
}

function loadGoogleMaps(apiKey: string) {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (mapsLoader) return mapsLoader;
  mapsLoader = new Promise<GoogleMapsNamespace>((resolve, reject) => {
    const callbackName = "__namoGoogleMapsReady";
    window[callbackName] = () => {
      if (window.google?.maps) resolve(window.google);
      else reject(new Error("Google Maps did not initialise"));
      delete window[callbackName];
    };
    const script = document.createElement("script");
    script.id = "namo-google-maps";
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async&callback=${callbackName}`;
    script.onerror = () => reject(new Error("Google Maps could not be loaded"));
    document.head.appendChild(script);
  });
  return mapsLoader;
}

function postalCode(result: GeocoderResult) {
  return result.address_components.find((component) => component.types.includes("postal_code"))?.long_name ?? "";
}

function countryCode(result: GeocoderResult) {
  return result.address_components.find((component) => component.types.includes("country"))?.short_name ?? "";
}

function geocode(geocoder: GeocoderLike, request: Record<string, unknown>) {
  return new Promise<GeocoderResult[]>((resolve, reject) => {
    geocoder.geocode(request, (results, status) => {
      if (status === "OK" && results) resolve(results);
      else if (status === "ZERO_RESULTS") resolve([]);
      else reject(new Error(status));
    });
  });
}

export function PremisesMap({ value, onChange, disabled = false }: { value: PremisesLocation; onChange: (next: PremisesLocation) => void; disabled?: boolean }) {
  const mapNode = useRef<HTMLDivElement | null>(null);
  const map = useRef<MapLike | null>(null);
  const marker = useRef<MarkerLike | null>(null);
  const geocoder = useRef<GeocoderLike | null>(null);
  const valueRef = useRef(value);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? "";
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [message, setMessage] = useState(apiKey ? "" : "Google Maps is not configured for this deployment. Contact the administrator before confirming a premises.");
  const [localityWarning, setLocalityWarning] = useState("");

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  function checkLocality(displayName: string) {
    const expected = [valueRef.current.name, valueRef.current.county].filter(Boolean) as string[];
    const normalized = displayName.toLocaleLowerCase();
    const missing = expected.filter((part) => !normalized.includes(part.toLocaleLowerCase()));
    setLocalityWarning(missing.length
      ? `Check this pin: Google’s mapped address does not include ${missing.join(" or ")}. Confirm it against the property details before continuing.`
      : "");
  }

  async function checkPointLocality(lat: number, lng: number) {
    if (!geocoder.current || (!valueRef.current.name && !valueRef.current.county)) return;
    try {
      const matches = await geocode(geocoder.current, { location: { lat, lng } });
      if (matches[0]?.formatted_address) checkLocality(matches[0].formatted_address);
    } catch {
      // Locality checking is advisory; the user can confirm against property documents.
    }
  }

  function updatePoint(lat: number, lng: number, adjusted: boolean, source: string) {
    const next = {
      ...valueRef.current,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
      location_source: source,
      pin_adjusted: adjusted,
      location_confirmed: false,
    };
    valueRef.current = next;
    onChange(next);
    void checkPointLocality(lat, lng);
  }

  useEffect(() => {
    if (!mapNode.current || map.current) return;
    if (!apiKey) return;
    let active = true;
    let mapClick: { remove: () => void } | null = null;
    let markerDrag: { remove: () => void } | null = null;
    void loadGoogleMaps(apiKey).then((google) => {
      if (!active || !mapNode.current) return;
      const initial = valueRef.current.latitude && valueRef.current.longitude
        ? { lat: Number(valueRef.current.latitude), lng: Number(valueRef.current.longitude) }
        : irelandCentre;
      const nextMap = new google.maps.Map(mapNode.current, {
        center: initial,
        zoom: valueRef.current.latitude ? 17 : 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: "cooperative",
      });
      const nextMarker = new google.maps.Marker({
        map: nextMap,
        position: valueRef.current.latitude ? initial : undefined,
        draggable: !disabled,
        title: "Proposed NAMO premises",
      });
      if (!disabled) {
        mapClick = nextMap.addListener("click", (event) => {
          if (event.latLng) updatePoint(event.latLng.lat(), event.latLng.lng(), true, "User-positioned Google Maps pin");
        });
        markerDrag = nextMarker.addListener("dragend", () => {
          const point = nextMarker.getPosition();
          if (point) updatePoint(point.lat(), point.lng(), true, "User-adjusted Google Maps pin");
        });
      }
      map.current = nextMap;
      marker.current = nextMarker;
      geocoder.current = new google.maps.Geocoder();
      setMapReady(true);
    }).catch(() => setMessage("Google Maps could not be loaded. Refresh the page or contact the administrator."));
    return () => {
      active = false;
      mapClick?.remove();
      markerDrag?.remove();
      marker.current?.setMap(null);
      map.current = null;
      marker.current = null;
      geocoder.current = null;
    };
  // The map is intentionally created once for this form instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current || !marker.current || !value.latitude || !value.longitude) return;
    const point = { lat: Number(value.latitude), lng: Number(value.longitude) };
    marker.current.setPosition(point);
    map.current.setCenter(point);
    map.current.setZoom(17);
  }, [value.latitude, value.longitude]);

  async function search() {
    if (!geocoder.current || !mapReady) {
      setMessage("The premises map is still loading. Try again in a moment.");
      return;
    }
    const hasEircode = validEircode(value.eircode);
    if (!hasEircode && !value.site_address.trim()) {
      setResults([]);
      setMessage("Enter a valid Eircode or a premises name and address first.");
      return;
    }
    setSearching(true);
    setMessage("");
    setResults([]);
    try {
      const enteredCode = normalizeEircode(value.eircode);
      const request = hasEircode
        ? { address: `${value.eircode}, Ireland`, componentRestrictions: { country: "IE", postalCode: value.eircode } }
        : { address: [value.site_address, value.name, value.county, "Ireland"].filter(Boolean).join(", "), componentRestrictions: { country: "IE" } };
      const data = await geocode(geocoder.current, request);
      const safeResults = data
        .filter((result) => countryCode(result) === "IE")
        .filter((result) => !hasEircode || normalizeEircode(postalCode(result)) === enteredCode)
        .slice(0, 5)
        .map((result) => ({
          place_id: result.place_id,
          display_name: result.formatted_address,
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
          postal_code: postalCode(result),
        }));
      setResults(safeResults);
      if (!safeResults.length) {
        setMessage(hasEircode
          ? "Google did not return an exact match for this Eircode. Check the code and premises address; no substitute location has been selected."
          : "No address match was found within Ireland. Add the premises name, street or retail centre and try again.");
      }
    } catch {
      setMessage("Google address search is temporarily unavailable. No location has been selected.");
    } finally {
      setSearching(false);
    }
  }

  function choose(result: SearchResult) {
    const next = {
      ...valueRef.current,
      site_address: value.site_address || result.display_name,
      latitude: result.lat.toFixed(6),
      longitude: result.lng.toFixed(6),
      location_source: result.postal_code ? "Google Maps exact Eircode match" : "Google Maps address match",
      pin_adjusted: false,
      location_confirmed: false,
    };
    valueRef.current = next;
    onChange(next);
    setResults([]);
    setMessage("Exact match found. Check the unit carefully, move the pin if needed, then confirm the premises.");
    checkLocality(result.display_name);
    map.current?.setCenter({ lat: result.lat, lng: result.lng });
    map.current?.setZoom(18);
  }

  const hasPoint = Boolean(value.latitude && value.longitude);
  return <div className="premises-map-shell">
    {!disabled && <div className="map-search-row"><button type="button" className="secondary-button" onClick={search} disabled={searching || !mapReady}>{searching ? "Searching…" : "Find exact premises"}</button><small>Google search prioritises an exact Eircode match. It will not substitute a different town or postcode.</small></div>}
    {results.length > 0 && <div className="map-results" role="listbox" aria-label="Address matches">{results.map((result) => <button type="button" key={result.place_id} onClick={() => choose(result)}><strong>{result.display_name}</strong><span>Use this location</span></button>)}</div>}
    {message && <p className="map-message" role="status">{message}</p>}
    {localityWarning && <p className="map-message warning" role="alert">{localityWarning}</p>}
    <div ref={mapNode} className="premises-map" aria-label="Exact premises map" />
    <div className="map-confirm-row"><div><strong>{hasPoint ? `${value.latitude}, ${value.longitude}` : "No exact point selected"}</strong><span>{value.location_source || "Search for the exact premises or click the map"}</span></div>{!disabled && <button type="button" className={value.location_confirmed ? "confirm-location confirmed" : "confirm-location"} disabled={!hasPoint || (Boolean(value.eircode) && !validEircode(value.eircode))} onClick={() => onChange({ ...value, location_confirmed: !value.location_confirmed })}>{value.location_confirmed ? "✓ Exact premises confirmed" : "Confirm exact premises"}</button>}</div>
    <p className="map-disclaimer"><strong>User-confirmed location.</strong> Google Maps is used to match the address and Eircode, but this is not an official Eircode validation service. Confirm the unit against the property brochure or a site visit.</p>
  </div>;
}
